import React, { useState } from 'react';
import { Badge } from 'react-bootstrap';

export type TabKey =
  | 'orders'
  | 'contacts'
  | 'reviews'
  | 'quotes'
  | 'users'
  | 'pricing'
  | 'products'
  | 'jobcards'
  | 'inventory'
  | 'attendance'
  | 'payslips'
  | 'estimation'
  | 'invoices'
  | 'customers'
  | 'sales'
  | 'purchase'
  | 'expenses'
  | 'tasks'
  | 'bankstatement';


interface AdminTabNavigationProps {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
  unreadContactsCount: number;
  pendingQuotesCount: number;
  activeJobCardsCount: number;
  lowStockItemsCount?: number;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavItem {
  key: TabKey;
  label: string;
  icon: string;
  badge?: number;
  badgeVariant?: string;
  adminOnly?: boolean; // admin or admin-manager
  adminManagerOnly?: boolean; // admin-manager only
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const AdminTabNavigation: React.FC<AdminTabNavigationProps> = ({
  activeTab,
  onTabChange,
  unreadContactsCount,
  pendingQuotesCount,
  activeJobCardsCount,
  lowStockItemsCount = 0,
  isCollapsed: externalIsCollapsed,
  onToggleCollapse,
}) => {
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const isCollapsed = externalIsCollapsed !== undefined ? externalIsCollapsed : internalIsCollapsed;

  const handleToggle = () => {
    if (onToggleCollapse) {
      onToggleCollapse();
    } else {
      setInternalIsCollapsed(prev => !prev);
    }
  };

  const role = localStorage.getItem('role') || 'customer';

  // Grouped Navigation Modules
  const groups: NavGroup[] = [
    {
      title: 'OPERATIONS',
      items: [
        { key: 'orders', label: 'Orders', icon: 'fas fa-file-invoice' },
        { key: 'jobcards', label: 'Job Cards', icon: 'fas fa-print', badge: activeJobCardsCount, badgeVariant: 'danger' },
        { key: 'invoices', label: 'Invoices', icon: 'fas fa-receipt' },
        { key: 'estimation', label: 'Estimation', icon: 'fas fa-file-invoice-dollar' },
        { key: 'tasks', label: 'Tasks', icon: 'fas fa-tasks', adminManagerOnly: true },
      ],
    },
    {
      title: 'CATALOG & STOCK',
      items: [
        { key: 'products', label: 'Products', icon: 'fas fa-cog' },
        { key: 'inventory', label: 'Inventory', icon: 'fas fa-boxes', badge: lowStockItemsCount, badgeVariant: 'danger' },
        { key: 'pricing', label: 'Pricing', icon: 'fas fa-tags', adminManagerOnly: true },
      ],
    },
    {
      title: 'PEOPLE & HR',
      items: [
        { key: 'customers', label: 'Customers', icon: 'fas fa-address-book' },
        { key: 'users', label: 'Staffs', icon: 'fas fa-users', adminManagerOnly: true },
        { key: 'attendance', label: 'Attendance', icon: 'fas fa-calendar-check', adminManagerOnly: true },
        { key: 'payslips', label: 'Payslips', icon: 'fas fa-money-check-alt', adminManagerOnly: true },
      ],
    },
    {
      title: 'CRM & ENQUIRIES',
      items: [
        { key: 'contacts', label: 'Enquiries', icon: 'far fa-comments', badge: unreadContactsCount, badgeVariant: 'danger' },
        { key: 'quotes', label: 'Quotes', icon: 'fas fa-calculator', badge: pendingQuotesCount, badgeVariant: 'warning' },
        { key: 'reviews', label: 'Reviews', icon: 'far fa-star', adminManagerOnly: true },
      ],
    },
    {
      title: 'FINANCE & ANALYTICS',
      items: [
        { key: 'sales', label: 'Sales Tracker', icon: 'fas fa-chart-line', adminManagerOnly: true },
        { key: 'purchase', label: 'Purchases', icon: 'fas fa-shopping-bag', adminManagerOnly: true },
        { key: 'expenses', label: 'Expenses', icon: 'fas fa-wallet', adminManagerOnly: true },
        { key: 'bankstatement', label: 'Bank Statement', icon: 'fas fa-university', adminManagerOnly: true },
      ],
    },
  ];


  const filterItem = (item: NavItem) => {
    if (role === 'admin-manager') return true;
    if (role === 'admin') return !item.adminManagerOnly;
    if (role === 'manager') return item.key === 'attendance' || item.key === 'jobcards' || item.key === 'tasks' || item.key === 'orders';
    return false;
  };

  return (
    <aside
      style={{
        width: isCollapsed ? '72px' : '260px',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        padding: isCollapsed ? '16px 8px' : '16px 12px',
        display: 'flex',
        flexDirection: 'column',
        userSelect: 'none',
        flexShrink: 0,
        height: 'fit-content',
        position: 'sticky',
        top: '90px',
      }}
    >
      {/* Header & Toggle Button */}
      <div
        className="d-flex align-items-center justify-content-between mb-3 pb-2 border-bottom"
        style={{ padding: isCollapsed ? '0 4px' : '0 8px' }}
      >
        {!isCollapsed && (
          <div className="d-flex align-items-center gap-2">
            <i className="fas fa-th-large text-primary" style={{ fontSize: '1rem' }} />
            <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', letterSpacing: '0.02em' }}>
              Menu Modules
            </span>
          </div>
        )}
        <button
          onClick={handleToggle}
          style={{
            background: '#f1f5f9',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
            margin: isCollapsed ? '0 auto' : '0',
          }}
          title={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#e2e8f0'; (e.currentTarget as HTMLElement).style.color = '#1e293b'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#f1f5f9'; (e.currentTarget as HTMLElement).style.color = '#64748b'; }}
        >
          <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'left'}`} style={{ fontSize: '0.8rem' }} />
        </button>
      </div>

      {/* Navigation Groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto', maxHeight: 'calc(100vh - 160px)' }}>
        {groups.map((group, groupIdx) => {
          const visibleItems = group.items.filter(filterItem);
          if (visibleItems.length === 0) return null;

          return (
            <div key={groupIdx}>
              {/* Group Title */}
              {!isCollapsed ? (
                <div
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: '#94a3b8',
                    letterSpacing: '0.08em',
                    padding: '0 10px',
                    marginBottom: '6px',
                  }}
                >
                  {group.title}
                </div>
              ) : (
                <div style={{ height: '1px', background: '#f1f5f9', margin: '4px 0 8px 0' }} />
              )}

              {/* Items List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {visibleItems.map(item => {
                  const isActive = activeTab === item.key;
                  const hasBadge = item.badge && item.badge > 0;

                  return (
                    <button
                      key={item.key}
                      onClick={() => onTabChange(item.key)}
                      title={isCollapsed ? item.label : undefined}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isCollapsed ? 'center' : 'between',
                        width: '100%',
                        padding: isCollapsed ? '10px 0' : '9px 12px',
                        borderRadius: '10px',
                        border: 'none',
                        background: isActive
                          ? 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)'
                          : 'transparent',
                        color: isActive ? '#ffffff' : '#475569',
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.18s ease-in-out',
                        position: 'relative',
                        boxShadow: isActive ? '0 4px 12px rgba(233,30,140,0.25)' : 'none',
                      }}
                      onMouseEnter={e => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background = '#f8fafc';
                          (e.currentTarget as HTMLElement).style.color = '#1e293b';
                        }
                      }}
                      onMouseLeave={e => {
                        if (!isActive) {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                          (e.currentTarget as HTMLElement).style.color = '#475569';
                        }
                      }}
                    >
                      {/* Icon */}
                      <div
                        style={{
                          width: isCollapsed ? 'auto' : '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1rem',
                        }}
                      >
                        <i className={item.icon} />
                      </div>

                      {/* Label when expanded */}
                      {!isCollapsed && (
                        <span style={{ marginLeft: '10px', flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.label}
                        </span>
                      )}

                      {/* Badge */}
                      {hasBadge && (
                        <Badge
                          bg={item.badgeVariant || 'danger'}
                          text={item.badgeVariant === 'warning' ? 'dark' : undefined}
                          pill
                          style={{
                            fontSize: '0.65rem',
                            padding: '3px 7px',
                            position: isCollapsed ? 'absolute' : 'static',
                            top: isCollapsed ? '4px' : 'auto',
                            right: isCollapsed ? '4px' : 'auto',
                            marginLeft: isCollapsed ? '0' : 'auto',
                          }}
                        >
                          {item.badge}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default AdminTabNavigation;
